import { getMemoOptions, memo } from "@tanstack/react-table";

/**
 * Custom row model implementation for handling hierarchical data expansion in TanStack Table.
 *
 *
 * @param {Table} table - The TanStack Table instance
 * @returns {Function} A memoized function that computes the expanded row model
 *
 * @remarks
 * - Rows must have a `level` property in their original data to define hierarchy (defaults to 0)
 * - Parent rows are those followed by rows with a higher level value
 * - Collapsed parents hide all their descendants (direct and nested children)
 * - Supports both boolean (expand all) and object (specific rows) expansion states
 * - Maintains the original flatRows and rowsById references for compatibility
 */
export const getExpandedRowModel = (table) => {
  return memo(
    () => [table.getState().expanded, table.getPreExpandedRowModel()],
    (expandedState, rowModel) => {
      if (!rowModel.rows.length) return rowModel;

      const isExpandAll = expandedState === true;
      const expandedMap = (isExpandAll ? null : expandedState) || {};

      const rows = [];
      const collapsedAncestors = new Set();

      for (let i = 0; i < rowModel.rows.length; i++) {
        const row = rowModel.rows[i];
        const level = row.original.level ?? 0;

        // Clear collapsed markers at or below current level
        for (const lvl of Array.from(collapsedAncestors)) {
          if (lvl >= level) {
            collapsedAncestors.delete(lvl);
          }
        }

        // Hidden if any smaller level is collapsed
        let hiddenByAncestor = false;
        for (const lvl of collapsedAncestors) {
          if (lvl < level) {
            hiddenByAncestor = true;
            break;
          }
        }
        if (hiddenByAncestor) {
          continue;
        }

        rows.push(row);

        const nextRow = rowModel.rows[i + 1];
        const canExpand = nextRow?.original?.level > level;

        if (canExpand) {
          const rowId = row.id;
          const isExpanded = isExpandAll || !!expandedMap?.[rowId];
          if (!isExpanded) {
            collapsedAncestors.add(level);
          }
        }
      }

      return {
        rows,
        flatRows: rowModel.flatRows,
        rowsById: rowModel.rowsById,
      };
    },
    getMemoOptions(table.options, "debugTable", "getExpandedRowModel")
  );
};

export default getExpandedRowModel;
